import React, { useState, useEffect } from 'react';
import type { Zone, ZoneAnchorObject, ZoningVariant } from '../types.ts';
import { AVAILABLE_ZONING_MODELS } from '../types.ts';
import { ZONE_DEFINITIONS } from '../config.ts';

interface ZoningSectionProps {
  prompt: string;
  onPromptChange: (newPrompt: string) => void;
  model: string;
  onModelChange: (newModel: string) => void;
  onGenerateZones: (customRules?: string) => void;
  onClearZones: () => void;
  isZoning: boolean;
  zones: Zone[];
  variants?: ZoningVariant[];
  activeVariantIndex?: number;
  onSelectVariant?: (index: number) => void;
  zoneAnchors?: ZoneAnchorObject[];
  showZoneAnchors?: boolean;
  onToggleShowZoneAnchors?: (show: boolean) => void;
  includeAllSkills?: boolean;
  onToggleIncludeAllSkills?: (include: boolean) => void;
  onFocusAnchor?: (anchorId: string | null) => void;
  focusedAnchorId?: string | null;
  aiExplanation?: string;
  errorMessage?: string;
  backendUrl: string;
  showZonesInPreview: boolean;
  onToggleShowZones: (show: boolean) => void;
  onFocusZone?: (zoneId: string | null) => void;
  focusedZoneId?: string | null;
}

const ZONING_PRESETS = [
  {
    label: '🛋️ Obývák + 🍽️ Jídelna + 🍳 Kuchyň',
    prompt: 'Otevřený koncept: funkční kuchyňská zóna s přímou návazností na jídelní kout a samostatná obývací zóna s pohovkou orientovanou k TV.'
  },
  {
    label: '🛋️ Obývák + 💻 Pracovní kout + 🌿 Relax',
    prompt: 'Vytvoř prostorný obývací pokoj pro setkávání rodiny, tichý pracovní kout se stolem u bočního denního světla a klidné čtecí zákoutí s křeslem.'
  },
  {
    label: '🍽️ Jídelna jako spojovací článek',
    prompt: 'Uspořádej prostor tak, aby jídelní stůl tvořil přirozený spojovací článek mezi kuchyní a obývacím prostorem a zachovej přímé průchody.'
  },
  {
    label: '🧸 Dětská herna + 🛋️ Rodinný obývák',
    prompt: 'Bezpečné rodinné uspořádání: souvislá herní plocha pro děti s nízkým úložným regálem v přímém dohledu z kuchyně i pohovky.'
  }
];

export function ZoningSection({
  prompt,
  onPromptChange,
  model,
  onModelChange,
  onGenerateZones,
  onClearZones,
  isZoning,
  zones,
  variants = [],
  activeVariantIndex = 0,
  onSelectVariant,
  zoneAnchors = [],
  showZoneAnchors = true,
  onToggleShowZoneAnchors,
  includeAllSkills = false,
  onToggleIncludeAllSkills,
  onFocusAnchor,
  focusedAnchorId,
  aiExplanation,
  errorMessage,
  backendUrl,
  showZonesInPreview,
  onToggleShowZones,
  onFocusZone,
  focusedZoneId
}: ZoningSectionProps) {
  // Rules editor state
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesContent, setRulesContent] = useState('');
  const [isSavingRules, setIsSavingRules] = useState(false);
  const [rulesSavedMessage, setRulesSavedMessage] = useState('');

  // Fetch current server rules
  useEffect(() => {
    fetch(`${backendUrl}/api/zoning/rules`)
      .then(res => res.json())
      .then(data => {
        if (data.content) {
          setRulesContent(data.content);
        }
      })
      .catch(err => console.warn('Could not load zoning rules:', err));
  }, [backendUrl]);

  const handleSaveRules = async () => {
    setIsSavingRules(true);
    setRulesSavedMessage('');
    try {
      const res = await fetch(`${backendUrl}/api/zoning/rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: rulesContent })
      });
      if (res.ok) {
        setRulesSavedMessage('Rules saved successfully to server!');
        setTimeout(() => setRulesSavedMessage(''), 4000);
      } else {
        throw new Error('Failed to save rules to server.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSavingRules(false);
    }
  };

  const handleRunZoning = () => {
    onGenerateZones(rulesContent);
  };

  const activeVariant = variants[activeVariantIndex];

  return (
    <section style={{
      backgroundColor: '#1e1e1e',
      border: '1px solid #333333',
      borderRadius: '12px',
      padding: '24px',
      marginTop: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #333333',
        paddingBottom: '14px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '34px',
            height: '34px',
            borderRadius: '8px',
            backgroundColor: 'rgba(59, 130, 246, 0.15)',
            color: '#60a5fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px'
          }}>
            📐
          </div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#f3f4f6', margin: 0 }}>
              Inteligentní zónování místnosti (Archie Zone Planner)
            </h2>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
              Pravidla ze složky <code>skills-zoning</code> + 3 varianty + zobrazení kotevních objektů
            </div>
          </div>
        </div>

        {/* Action links */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setRulesOpen(!rulesOpen)}
            style={{
              backgroundColor: rulesOpen ? 'rgba(168, 85, 247, 0.2)' : 'rgba(255, 255, 255, 0.05)',
              border: '1px solid #444444',
              color: rulesOpen ? '#c084fc' : '#e5e7eb',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            title="Zobrazit a upravit architektonická pravidla zónování"
          >
            📋 {rulesOpen ? 'Skrýt pravidla' : 'Pravidla zónování (Markdown)'}
          </button>

          {/* Open Documentation directly in a new tab */}
          <a
            href={`${backendUrl}/zoning-doc`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              color: '#93c5fd',
              padding: '6px 14px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              textDecoration: 'none',
              transition: 'all 0.2s'
            }}
            title="Otevřít dokumentaci ZONING.md v nové záložce"
          >
            📖 Dokumentace ZONING.md ↗
          </a>
        </div>
      </div>

      {/* Expandable Server Zoning Rules Editor */}
      {rulesOpen && (
        <div style={{
          backgroundColor: '#141414',
          border: '1px solid #2a2a2a',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#f3f4f6' }}>
                📋 Hlavní pravidla zónování (složka <code>skills-zoning</code>)
              </span>
              <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '10px' }}>
                Načítá se z <code>skills-zoning/*.md</code> a připojuje se ke každému promptu
              </span>
            </div>
            {rulesSavedMessage && (
              <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
                ✓ {rulesSavedMessage}
              </span>
            )}
          </div>

          <textarea
            value={rulesContent}
            onChange={e => setRulesContent(e.target.value)}
            rows={10}
            style={{
              width: '100%',
              backgroundColor: '#0d0d0d',
              border: '1px solid #333333',
              borderRadius: '6px',
              color: '#e5e7eb',
              padding: '12px',
              fontSize: '12px',
              fontFamily: 'JetBrains Mono, monospace',
              lineHeight: '1.5',
              boxSizing: 'border-box'
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={handleSaveRules}
              disabled={isSavingRules}
              style={{
                backgroundColor: '#2563eb',
                border: 'none',
                color: '#ffffff',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: isSavingRules ? 'wait' : 'pointer'
              }}
            >
              {isSavingRules ? 'Ukládám...' : '💾 Uložit pravidla'}
            </button>
          </div>
        </div>
      )}

      {/* Available Client Zones Chips */}
      <div>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Dostupný katalog zón (místnost nemusí obsahovat všechny):
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {Object.values(ZONE_DEFINITIONS).map(def => (
            <div
              key={def.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                border: `1px solid ${def.strokeColor}44`,
                color: '#e5e7eb'
              }}
              title={`${def.descriptionCs}\nKotvy: ${def.typicalFurniture.join(', ')}`}
            >
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: def.strokeColor
              }} />
              <span style={{ fontWeight: 500 }}>{def.nameCs}</span>
              <span style={{ fontSize: '10px', color: '#9ca3af' }}>({def.typicalFurniture.slice(0, 2).join(', ')}...)</span>
            </div>
          ))}
        </div>
      </div>

      {/* Prompt Input & Presets */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#e5e7eb' }}>
            Zadání pro zónování & preference uspořádání:
          </label>

          {/* Model Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>AI Model:</span>
            <select
              value={model}
              onChange={e => onModelChange(e.target.value)}
              style={{
                backgroundColor: '#121212',
                border: '1px solid #333333',
                color: '#f3f4f6',
                padding: '5px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              {AVAILABLE_ZONING_MODELS.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick Presets */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {ZONING_PRESETS.map((preset, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onPromptChange(preset.prompt)}
              style={{
                backgroundColor: '#121212',
                border: '1px solid #2a2a2a',
                color: '#9ca3af',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '11px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <textarea
          id="zoning-prompt-textarea"
          value={prompt}
          onChange={e => onPromptChange(e.target.value)}
          placeholder="Popište, jak si představujete funkční rozdělení prostoru (např. Prostorný obývací pokoj s TV stěnou na západní stěně, jídelní kout navazující na kuchyň a pracovní kout u okna)..."
          rows={3}
          style={{
            width: '100%',
            backgroundColor: '#121212',
            border: '1px solid #333333',
            borderRadius: '8px',
            color: '#f3f4f6',
            padding: '12px',
            fontSize: '13px',
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box'
          }}
        />

        {/* Checkbox: Include all skills from skills folder */}
        <div style={{
          backgroundColor: '#161616',
          padding: '10px 14px',
          borderRadius: '8px',
          border: '1px solid #2c2c2c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#e5e7eb' }}>
            <input
              type="checkbox"
              checked={includeAllSkills}
              onChange={e => onToggleIncludeAllSkills && onToggleIncludeAllSkills(e.target.checked)}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
            <div>
              <span style={{ fontWeight: 600 }}>Zahrnout do zónování všechny design skilly (složka <code>skills</code>)</span>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                Zahrne 18 pravidel interiérového designu (ergonomie, průchody, materiály, osvětlení atd.) původně určených pro chatbota.
              </div>
            </div>
          </label>
          <span style={{
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '12px',
            backgroundColor: includeAllSkills ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.05)',
            color: includeAllSkills ? '#34d399' : '#9ca3af',
            fontWeight: 600
          }}>
            {includeAllSkills ? 'Aktivní (18 skillů)' : 'Vypnuto (jen skills-zoning)'}
          </span>
        </div>

        {/* Action Buttons & Canvas Preview Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleRunZoning}
              disabled={isZoning}
              style={{
                backgroundColor: isZoning ? '#1d4ed8' : '#2563eb',
                border: 'none',
                color: '#ffffff',
                padding: '10px 24px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: isZoning ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
                transition: 'all 0.2s'
              }}
            >
              {isZoning ? (
                <>
                  <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                  Generuji 3 varianty zónování...
                </>
              ) : (
                <>
                  <span>⚡</span>
                  Generovat 3 varianty zón
                </>
              )}
            </button>

            {zones.length > 0 && (
              <button
                type="button"
                onClick={onClearZones}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #444444',
                  color: '#9ca3af',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Vymazat zóny
              </button>
            )}
          </div>

          {/* Toggle Switches for Preview SVG */}
          {zones.length > 0 && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#e5e7eb' }}>
                <input
                  type="checkbox"
                  checked={showZonesInPreview}
                  onChange={e => onToggleShowZones(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span>Zobrazit zóny (polygony)</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#38bdf8' }}>
                <input
                  type="checkbox"
                  checked={showZoneAnchors}
                  onChange={e => onToggleShowZoneAnchors && onToggleShowZoneAnchors(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span>⚓ Zobrazit kotevní objekty (anchors)</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Error display */}
      {errorMessage && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          color: '#fca5a5',
          fontSize: '13px'
        }}>
          <strong>Chyba:</strong> {errorMessage}
        </div>
      )}

      {/* Results Section: 3 Variants Switcher, Active Zones & Anchors */}
      {zones.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          backgroundColor: '#171717',
          padding: '18px',
          borderRadius: '10px',
          border: '1px solid #282828'
        }}>
          {/* Variant Selector Tabs */}
          {variants.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#f3f4f6' }}>
                  Architektonické varianty zónování ({variants.length}):
                </div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                  Každá varianta nabízí odlišnou prostorovou topologii a vztahy kotev
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${variants.length}, 1fr)`, gap: '10px' }}>
                {variants.map((v, idx) => {
                  const isSelected = activeVariantIndex === idx;
                  const confidenceBadge = v.confidence === 'high' ? '🟢 Vysoká jistota' : v.confidence === 'medium' ? '🟡 Střední jistota' : '🔴 Nízká jistota';
                  return (
                    <button
                      key={v.variantId || idx}
                      type="button"
                      onClick={() => onSelectVariant && onSelectVariant(idx)}
                      style={{
                        backgroundColor: isSelected ? 'rgba(37, 99, 235, 0.18)' : '#202020',
                        border: isSelected ? '2px solid #3b82f6' : '1px solid #333333',
                        borderRadius: '8px',
                        padding: '12px 14px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        transition: 'all 0.2s',
                        boxShadow: isSelected ? '0 0 12px rgba(59, 130, 246, 0.25)' : 'none'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{
                          fontWeight: 700,
                          fontSize: '13px',
                          color: isSelected ? '#60a5fa' : '#e5e7eb'
                        }}>
                          {v.title || `Varianta ${idx + 1}`}
                        </span>
                        {typeof v.score === 'number' && (
                          <span style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: '10px',
                            backgroundColor: isSelected ? '#2563eb' : '#333333',
                            color: '#ffffff'
                          }}>
                            {v.score} b.
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af' }}>
                        <span>{v.selectedZoneTypes.length} zón, {v.zoneAnchors.length} kotev</span>
                        <span>{confidenceBadge}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Active Variant Header / Detail */}
          {activeVariant && (
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.05)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
              borderRadius: '8px',
              padding: '14px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: '14px', color: '#93c5fd' }}>
                  💡 {activeVariant.title}
                </span>
                {typeof activeVariant.score === 'number' && (
                  <span style={{ fontSize: '12px', color: '#60a5fa', fontWeight: 600 }}>
                    Skóre návrhu: <strong>{activeVariant.score} / 100 bodů</strong>
                  </span>
                )}
              </div>

              <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: '1.6' }}>
                {activeVariant.description}
              </div>

              {activeVariant.tradeOffs && (
                <div style={{
                  marginTop: '4px',
                  padding: '8px 12px',
                  backgroundColor: 'rgba(234, 179, 8, 0.08)',
                  border: '1px solid rgba(234, 179, 8, 0.3)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#fef08a'
                }}>
                  <strong>⚖️ Hlavní kompromis / úskalí:</strong> {activeVariant.tradeOffs}
                </div>
              )}
            </div>
          )}

          {/* Zones Summary Cards */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#f3f4f6' }}>
                Vygenerované zóny ({zones.length}):
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                Kliknutím na kartu zóny zvýrazníte její polygon v náhledu
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
              {zones.map(zone => {
                const isFocused = focusedZoneId === zone.id;
                return (
                  <div
                    key={zone.id}
                    onClick={() => onFocusZone && onFocusZone(isFocused ? null : zone.id)}
                    style={{
                      backgroundColor: isFocused ? 'rgba(59, 130, 246, 0.15)' : '#222222',
                      border: `1px solid ${isFocused ? zone.strokeColor : '#333333'}`,
                      borderLeft: `4px solid ${zone.strokeColor}`,
                      borderRadius: '6px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: '#f3f4f6' }}>
                        {zone.name}
                      </span>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: zone.strokeColor,
                        backgroundColor: `${zone.strokeColor}18`,
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        {zone.areaPercent}%
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '12px', color: '#9ca3af' }}>
                      <span>Plocha: <strong style={{ color: '#e5e7eb' }}>{zone.area} m²</strong></span>
                      <span>Těžiště: [{(zone.centroid[0] / 1000).toFixed(1)}, {(zone.centroid[1] / 1000).toFixed(1)}]m</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Anchor Objects Section */}
          {zoneAnchors.length > 0 && (
            <div style={{
              backgroundColor: '#1b1b1b',
              padding: '14px',
              borderRadius: '8px',
              border: '1px solid #2e2e2e'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>⚓</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#f3f4f6' }}>
                    Použité kotevní objekty ({zoneAnchors.length})
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                  Objekty (fyzické i virtuální) sloužící jako kotevní body pro výpočet zón
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px' }}>
                {zoneAnchors.map(anchor => {
                  const zoneDef = ZONE_DEFINITIONS[anchor.zoneType] || ZONE_DEFINITIONS.living;
                  const isFocused = (focusedZoneId === anchor.zoneId) || (focusedAnchorId === anchor.id);

                  return (
                    <div
                      key={anchor.id}
                      onClick={() => onFocusAnchor && onFocusAnchor(isFocused ? null : anchor.id)}
                      style={{
                        backgroundColor: isFocused ? 'rgba(59, 130, 246, 0.15)' : '#242424',
                        border: `1px solid ${isFocused ? zoneDef.strokeColor : '#383838'}`,
                        borderLeft: `3px solid ${zoneDef.strokeColor}`,
                        borderRadius: '6px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: '12px', color: '#f3f4f6' }}>
                          ⚓ {anchor.name}
                        </span>
                        <span style={{
                          fontSize: '10px',
                          color: zoneDef.strokeColor,
                          backgroundColor: `${zoneDef.strokeColor}18`,
                          padding: '1px 6px',
                          borderRadius: '4px',
                          fontWeight: 600
                        }}>
                          {zoneDef.nameCs}
                        </span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '11px', color: '#9ca3af' }}>
                        <span>Rozměry: <strong style={{ color: '#e5e7eb' }}>{anchor.size.width}×{anchor.size.length}m</strong></span>
                        <span>Pozice: [<strong>{anchor.position.x.toFixed(1)}, {anchor.position.y.toFixed(1)}</strong>]m</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Explanation (Fallback if not shown in variant header) */}
          {aiExplanation && !activeVariant && (
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.06)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '6px',
              padding: '12px 14px',
              fontSize: '13px',
              color: '#dbeafe',
              lineHeight: '1.6'
            }}>
              <strong>💡 Zdůvodnění architekta (Gemini):</strong> {aiExplanation}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
